import React, { PropsWithChildren, ReactNode } from 'react';

import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';

import FraudWarning from '~/components/Users/History/FraudWarning';

import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';

import EmployeeUserList from '../EmployeeUserList';

type Props = PropsWithChildren<{
  user?: App.User;
  title: ReactNode;
  subtitle?: ReactNode;
  backButton?: string | boolean;
  color?: string;
  isAdmin?: boolean;
  sticky?: boolean;
  updatePreferredAgent?: (agent: string) => void;
}>;

export default function PageHeader({
  user,
  title,
  children,
  subtitle,
  backButton,
  isAdmin,
  sticky = false,
  updatePreferredAgent,
}: Props) {
  const history = useHistory();
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const showPreferredAgent = user && brand !== LE_BUSINESS_TRAVELLER;

  const backButtonProps = () => {
    if (typeof backButton === 'string') {
      return { to: backButton, component: Link };
    }

    return { onClick: goBack };
  };

  const goBack = () => {
    history.goBack();
  };

  return (
    <Box pt={1} pb={3} sx={sticky ? { position: 'sticky', top: 0, zIndex: 1000, backgroundColor: 'white' } : undefined}>
      <Stack direction="row" alignItems="center" spacing={2} mb={1}>
        {backButton && (
          <IconButton sx={{ border: 1 }} size="small" color="secondary" title="Back" {...backButtonProps()}>
            <ArrowBackIcon />
          </IconButton>
        )}
        {!subtitle && !user && (
          <Typography flex={1} component="h1" fontSize="2.5rem" fontWeight={500}>
            {title}
          </Typography>
        )}
        {!subtitle && user && (
          <Stack direction="column" gap={0.5}>
            <Typography flex={1} component="h1" fontSize="2.5rem" fontWeight={500}>
              {title}
            </Typography>
            <Typography component="h2" fontSize="1.5rem">
              {subtitle}
            </Typography>
            {showPreferredAgent && (
              <Typography component="h2" fontSize="1.5rem">
                Preferred Sales Agent:{' '}
                <EmployeeUserList user={user} isAdmin={isAdmin} onUpdatePreferredAgent={updatePreferredAgent} />
              </Typography>
            )}
          </Stack>
        )}

        {!!subtitle && (
          <Stack direction="column" gap={0.5}>
            <Typography flex={1} component="h1" fontSize="2.5rem" fontWeight={500}>
              {title}
            </Typography>
            <Typography component="h2" fontSize="1.5rem">
              {subtitle}
            </Typography>
            {showPreferredAgent && (
              <Typography component="h2" fontSize="1.5rem">
                Preferred Sales Agent:{' '}
                <EmployeeUserList user={user} isAdmin={isAdmin} onUpdatePreferredAgent={updatePreferredAgent} />
              </Typography>
            )}
          </Stack>
        )}

        {children && <Box>{children}</Box>}
      </Stack>

      <Divider />
      <FraudWarning userId={user?.id_member} />
    </Box>
  );
}
